import { useState, useEffect } from 'react';
import { UserProgram } from '../../types/user.response';
import SearchableDropdown from '../../components/shared/SearchableDropdown';
import { CatalogProgram, CatalogCollege } from '../../types/catalog.response';

interface YearOption {
    _id: string;
    name: string;
}

interface ProgramSelectionFormProps {
    programs: CatalogProgram[];
    colleges: CatalogCollege[];
    initialEntryYear?: string;
    onComplete: (programs: UserProgram[], collegeId: string, entryYear: string) => void;
    onBack?: () => void;
    loading?: boolean;
    submitLabel?: string;
}

export default function ProgramSelectionForm({
    programs,
    colleges,
    initialEntryYear = '',
    onComplete,
    onBack,
    loading: parentLoading = false,
    submitLabel = 'Finish'
}: ProgramSelectionFormProps) {
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [entryYear, setEntryYear] = useState(initialEntryYear);
    const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);

    const yearOptions: YearOption[] = [2023, 2024, 2025, 2026].map(year => ({
        _id: year.toString(),
        name: year.toString(),
    }));
    const [majors, setMajors] = useState<CatalogProgram[]>([]);
    const [minors, setMinors] = useState<CatalogProgram[]>([]);
    const [selectedCollege, setSelectedCollege] = useState<CatalogCollege | null>(null);
    const [selectedMajors, setSelectedMajors] = useState<UserProgram[]>([{
        programId: '',
        name: '',
        type: 'major',
        concentrationNames: []
    }]);
    const [selectedMinors, setSelectedMinors] = useState<UserProgram[]>([]);

    useEffect(() => {
        const majors = programs
            .filter(program => program.type === 'major')
            .sort((a, b) => a.name.localeCompare(b.name));
        const minors = programs
            .filter(program => program.type === 'minor')
            .sort((a, b) => a.name.localeCompare(b.name));

        setMinors(minors);

        const availableMajors = majors.filter(major =>
            major.colleges.some(college => college.collegeId === selectedCollege?.collegeId)
        );
        setMajors(availableMajors);
    }, [programs, selectedCollege]);

    const handleCollegeSelect = (collegeId: string) => {
        const college = colleges.find(c => c.collegeId === collegeId);
        if (!college) return;
        setSelectedCollege(college);
        setSubmitError(null);
    };

    const handleMajorSelect = (index: number, majorId: string) => {
        const major = majors.find(m => m.programId === majorId);
        if (!major) return;

        setSelectedMajors(prev => {
            const next = [...prev];
            next[index] = {
                programId: major.programId,
                name: major.name,
                type: 'major',
                concentrationNames: []
            };
            return next;
        });
        setSubmitError(null);
    };

    const handleMajorConcentrationSelect = (index: number, concentrationName: string) => {
        setSelectedMajors(prev => {
            const next = [...prev];
            next[index] = { ...next[index], concentrationNames: [concentrationName] };
            return next;
        });
        setSubmitError(null);
    };

    const handleMinorSelect = (index: number, minorId: string) => {
        const minor = minors.find(m => m.programId === minorId);
        if (!minor) return;

        setSelectedMinors(prev => {
            const next = [...prev];
            next[index] = {
                programId: minor.programId,
                name: minor.name,
                type: 'minor',
                concentrationNames: []
            };
            return next;
        });
        setSubmitError(null);
    };

    const handleMinorConcentrationSelect = (index: number, concentrationName: string) => {
        setSelectedMinors(prev => {
            const next = [...prev];
            next[index] = { ...next[index], concentrationNames: [concentrationName] };
            return next;
        });
        setSubmitError(null);
    };

    const addMajorDropdown = () => {
        setSelectedMajors(prev => [...prev, {
            programId: '',
            name: '',
            type: 'major',
            concentrationNames: []
        }]);
    };

    const removeMajorDropdown = (index: number) => {
        setSelectedMajors(prev => prev.filter((_, i) => i !== index));
    };

    const addMinorDropdown = () => {
        setSelectedMinors(prev => [...prev, {
            programId: '',
            name: '',
            type: 'minor',
            concentrationNames: []
        }]);
    };

    const removeMinorDropdown = (index: number) => {
        setSelectedMinors(prev => prev.filter((_, i) => i !== index));
    };

    const validateForm = () => {
        if (!entryYear) {
            return 'Please select your entry year';
        }

        if (!selectedCollege) {
            return 'Please select a college';
        }

        if (selectedMajors.some(major => !major.programId)) {
            return 'Please select all majors';
        }

        for (let i = 0; i < selectedMajors.length; i++) {
            const selectedMajor = selectedMajors[i];
            const majorData = majors.find(m => m.programId === selectedMajor.programId);
            
            if (majorData?.concentrations && majorData.concentrations.length > 0) {
                if (selectedMajor.concentrationNames.length === 0) {
                    return `Please select a concentration for ${majorData.name}`;
                }
            }
        }

        if (selectedMinors.some(minor => !minor.programId)) {
            return 'Please select all minors or remove empty minor selections';
        }

        for (let i = 0; i < selectedMinors.length; i++) {
            const selectedMinor = selectedMinors[i];
            const minorData = minors.find(m => m.programId === selectedMinor.programId);
            
            if (minorData?.concentrations && minorData.concentrations.length > 0) {
                if (selectedMinor.concentrationNames.length === 0) {
                    return `Please select a concentration for ${minorData.name}`;
                }
            }
        }

        return null;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const validationError = validateForm();
        if (validationError) {
            setSubmitError(validationError);
            return;
        }
        const allPrograms = [...selectedMajors, ...selectedMinors];
        onComplete(allPrograms, selectedCollege!.collegeId, entryYear);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-8 mb-16">

            {/* Entry Year Section */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Entry Year
                </label>
                <SearchableDropdown
                    value={entryYear}
                    options={yearOptions}
                    onChange={(yearId) => {
                        setEntryYear(yearId);
                        setSubmitError(null);
                    }}
                    excludeIds={[]}
                    placeholder="Select your entry year..."
                    noResultsText="No years found"
                    onOpenChange={setIsYearDropdownOpen}
                />
            </div>

            {/* Error display */}
            {submitError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    <p className="text-sm">{submitError}</p>
                </div>
            )}
            
            {/* College Section */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    College
                </label>
                <SearchableDropdown
                    value={selectedCollege?.collegeId || ''}
                    options={colleges.map(c => ({ _id: c.collegeId, name: c.name }))}
                    onChange={handleCollegeSelect}
                    excludeIds={[]}
                    placeholder="Search for a college..."
                    noResultsText="No colleges found"
                />
            </div>

            {/* Majors Section */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Major
                </label>
                <div className="space-y-4 mb-4">
                    {selectedMajors.map((selectedMajor, index) => {
                        const majorData = majors.find(m => m.programId === selectedMajor.programId);
                        const hasConcentrations = majorData?.concentrations && majorData.concentrations.length > 0;
                        
                        return (
                            <div key={index} className="py-3">
                                <div className="flex items-center gap-2">
                                    <SearchableDropdown
                                        value={selectedMajor.programId}
                                        options={majors.map(m => ({ _id: m.programId, name: m.name }))}
                                        onChange={(majorId) => handleMajorSelect(index, majorId)}
                                        excludeIds={selectedMajors
                                            .filter((_, i) => i !== index)
                                            .map(m => m.programId)
                                            .filter(Boolean)}
                                        placeholder="Search for a major..."
                                        noResultsText="No majors found"
                                    />
                                    {index > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => removeMajorDropdown(index)}
                                            className="text-gray-400 hover:text-gray-600"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                
                                {/* Concentration Dropdown */}
                                {hasConcentrations && selectedMajor.programId && (
                                    <div className="mt-3 pl-8">
                                        <SearchableDropdown
                                            value={selectedMajor.concentrationNames[0] || ''}
                                            options={(majorData.concentrations || []).map(name => ({ _id: name, name }))}
                                            onChange={(concentration) => handleMajorConcentrationSelect(index, concentration)}
                                            excludeIds={[]}
                                            placeholder="Select a concentration..."
                                            noResultsText="No concentrations found"
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                
                {/* Add Another Major Button */}
                <button
                    type="button"
                    onClick={addMajorDropdown}
                    className="text-gray-400 hover:text-gray-600 text-sm flex items-center gap-1 mt-2"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Add another major
                </button>
            </div>

            {/* Minors Section */}
            <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                Minor (optional)
            </label>
                <div className="space-y-4 mb-4">
                    {selectedMinors.map((selectedMinor, index) => {
                        const minorData = minors.find(m => m.programId === selectedMinor.programId);
                        const hasConcentrations = minorData?.concentrations && minorData.concentrations.length > 0;
                        
                        return (
                            <div key={index} className="py-3">
                                <div className="flex items-center gap-2">
                                    <SearchableDropdown
                                        value={selectedMinor.programId}
                                        options={minors.map(m => ({ _id: m.programId, name: m.name }))}
                                        onChange={(minorId) => handleMinorSelect(index, minorId)}
                                        excludeIds={selectedMinors
                                            .filter((_, i) => i !== index)
                                            .map(m => m.programId)
                                            .filter(Boolean)}
                                        placeholder="Search for a minor..."
                                        noResultsText="No minors found"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeMinorDropdown(index)}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </div>
                                
                                {/* Concentration Dropdown */}
                                {hasConcentrations && selectedMinor.programId && (
                                    <div className="mt-3 pl-8">
                                        <SearchableDropdown
                                            value={selectedMinor.concentrationNames[0] || ''}
                                            options={(minorData.concentrations || []).map(name => ({ _id: name, name }))}
                                            onChange={(concentration) => handleMinorConcentrationSelect(index, concentration)}
                                            excludeIds={[]}
                                            placeholder="Select a concentration..."
                                            noResultsText="No concentrations found"
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                
                {/* Add Minor Button */}
                <button
                    type="button"
                    onClick={addMinorDropdown}
                    className="text-gray-400 hover:text-gray-600 text-sm flex items-center gap-1 mt-2"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Add a minor
                </button>
            </div>

            {/* Request program link */}
            <p className="text-sm text-gray-500">
                Don't see your program?{' '}
                <a
                    href="https://forms.gle/yGzNkoLcPaq8JZih9"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 underline hover:text-blue-700"
                >
                    Request it here
                </a>
            </p>

            {/* Back and Finish Buttons */}
            <div className="flex justify-between items-center">
                {onBack && (
                    <button
                        type="button"
                        onClick={onBack}
                        className="py-2 px-6 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Back
                    </button>
                )}
                <button
                    type="submit"
                    disabled={parentLoading || selectedMajors.length === 0}
                    className="py-2 px-6 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                    {parentLoading ? 'Loading...' : submitLabel}
                </button>
            </div>
        </form>
    );
}
